import { LinkCellRenderer } from "admin-app/components/cellRenderer";
import AddOrganizationForm from "admin-app/components/forms/add-organization";
import { TOrganizationLog } from "admin-app/models";
import { ORGANIZATION_LIST } from "admin-app/services/ciq-queries";
import { ColDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { Button } from "antd";
import CiqAgSearch from "components/ciq-ag-search";
import CiqLogPageHeader, {
  calculateLogPageStats
} from "components/ciq-log-page-header";
import { GridLoadingIndicator } from "components/widgets";
import { useQuerySubscription } from "hooks/common";
import { useMemo, useRef, useState, useEffect } from "react";

import {
  DATE_FORMAT_MMDDYYYY_HHMM_A,
  DateFilter,
  DateUtils
} from "utils/dateutils";
import { comparatorWithEmptyAtBottom } from "utils/grid-utils";
import { getGQL } from "utils/utils";

function OrganizationDashboard() {
  const gridRef = useRef<AgGridReact<any>>(null);
  const [openAddOrganizationModel, setOpenAddOrganizationModel] =
    useState(false);
  const [isGridReady, setGridReady] = useState(false);
  const [statsData, setStatsData] = useState<{
    displayCount: number;
    filterName: undefined | string;
  }>();

  const { query: qOrganizationQuery, sub: sOrganizationQuery } =
    getGQL(ORGANIZATION_LIST);
  const { data: OrganizationData } = useQuerySubscription<{
    organization: Array<TOrganizationLog>;
  }>(qOrganizationQuery, sOrganizationQuery, {});

  const columnDefs: ColDef[] = [
    {
      colId: "name",
      field: "name",
      headerName: "Organization Name",
      cellRenderer: LinkCellRenderer,
      tooltipField: "name"
    },
    {
      colId: "noOfSubcriptions",
      field: "organization_subscriptions_aggregate.aggregate.count",
      headerName: "No. Of Subscriptions",
      width: 230,
      tooltipField: "organization_subscriptions_aggregate.aggregate.count"
    },
    {
      colId: "create_by",
      headerName: "Created By",
      valueGetter: ({ data }) => {
        return `${data.created_by_user.first_name} ${data.created_by_user.last_name}`;
      },
      tooltipValueGetter: ({ value }) => value
    },
    {
      colId: "created_at",
      field: "created_at",
      headerName: "Created On",
      comparator: (value1, value2) =>
        DateFilter.customComparator(
          value1,
          value2,
          DATE_FORMAT_MMDDYYYY_HHMM_A
        ),
      valueGetter: ({ data }) =>
        DateUtils.format(data.created_at, DATE_FORMAT_MMDDYYYY_HHMM_A),
      tooltipValueGetter: ({ value }) => value
    }
  ];

  const defaultColDef = useMemo(() => {
    return {
      sortable: true,
      editable: false,
      filter: false,
      resizable: true,
      comparator: comparatorWithEmptyAtBottom
    };
  }, []);

  useEffect(() => {
    function handleResize() {
      if (isGridReady && gridRef && gridRef.current) {
        gridRef?.current?.api?.sizeColumnsToFit();
      }
    }
    window.addEventListener("resize", handleResize);
  }, [isGridReady]);

  const onFiltersApplied = () => {
    setStatsData(
      calculateLogPageStats({
        gridRef
      })
    );
  };

  const headerItems = [
    <CiqAgSearch
      key="Organizations_search"
      gridRef={gridRef}
      placeholder="Search Organizations"
    />,
    <Button
      key="Organizations_AddNew"
      type="primary"
      className="px-5"
      onClick={() => setOpenAddOrganizationModel(true)}
      data-testid="add-organization-button"
    >
      Add Organization
    </Button>
  ];

  return (
    <div className="flex flex-col h-full">
      <CiqLogPageHeader
        {...{
          entityName: "Organizations",
          titleParam: {
            title: "Organizations",
            totalCount: OrganizationData?.organization?.length,
            filterStats: statsData
          },
          gridRef,
          items: headerItems
        }}
      />

      <div className="grow flex-col ag-theme-alpine">
        <AgGridReact<TOrganizationLog>
          ref={gridRef}
          rowData={OrganizationData?.organization}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          headerHeight={36}
          loadingOverlayComponent={GridLoadingIndicator}
          onFilterChanged={onFiltersApplied}
          onGridReady={() => {
            setGridReady(true);
            gridRef.current?.api.sizeColumnsToFit();
          }}
          overlayNoRowsTemplate="There is no organizations created yet, please click 'Add Organization' button to create organizations."
        />
      </div>
      {openAddOrganizationModel && (
        <AddOrganizationForm
          open={openAddOrganizationModel}
          onClose={() => setOpenAddOrganizationModel(false)}
          data-testid="add-organization-form"
        />
      )}
    </div>
  );
}
export default OrganizationDashboard;
